'use client'

import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import { useMessage } from '@/src/context/message/message-context'
import { useMerchant } from '@/src/context/merchant/merchant-context'
import AlternativeLoginMethods from './_alternative-login-methods/page'
import { useCustomer } from '@/src/context/customer/customer-context'
import { CookieNameEnum } from '@/src/constants/cookie-names'
import '@/src/extensions/string'
import { deleteCookie, getCookie } from 'cookies-next'
import ActionButton from '@/src/components/misc/action-elements/action-button/action-button'
import ActionInput from '@/src/components/misc/action-elements/action-input/action-input'
import { CustomerService } from '@/src/services/merchant/customer-service'
import OtpCode from './_otp-code/page'
import BottomSheet from '@/src/components/helpers/overlays/bottom-sheet/bottom-sheet'
import { useRouter, useSearchParams } from 'next/navigation'
import { LoginFunctions, SignInProps, AuthCustomerProps } from './functions'
import LoginValidations, { CustomerLogin } from './validations'
import NavBackTitle from '@/src/components/misc/nav-back-title/nav-back-title'
import TermsOfUse from '@/src/components/misc/terms-of-use/terms-of-use'
import { useAppAccessToken } from '@/src/context/app-access-token/app-access-token-context'
import LoadingFullscreen from '@/src/components/loadings/loading-fullscreen/loading-fullscreen'
import ForgotPassword from './_forgot-password/page'

export default function LoginDefault() {
    const { showMessage } = useMessage()
    const { merchantSummary } = useMerchant()
    const { setCustomer } = useCustomer()
    const router = useRouter()
    const searchParams = useSearchParams()
    const { appAccessToken } = useAppAccessToken()

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [callbackUrl, setCallbackUrl] = useState<string>()

    const [otpCode, setOtpCode] = useState<string>()
    const [otpSuccess, setOtpSuccess] = useState<boolean>(false)
    const [isSendingOtp, setisSendingOtp] = useState<boolean>(false)
    const [resendingOtp, setResendingOtp] = useState<boolean>(false)
    const [isNewOtpSended, setisNewOtpSended] = useState<boolean>(false)
    const [canTryToLogin, setCanTryToLogin] = useState<boolean>(false)

    const [fullScreenLoading, setFullScreenLoading] = useState(false)

    const [forgotPassword, setForgotPassword] = useState<boolean>(false)

    const [dataValidation, setDataValidation] = useState<CustomerLogin>({
        email: email,
        password: password,
    })

    useEffect(() => {
        setDataValidation({
            email: email,
            password: password,
        })
    }, [email, password])

    const handleSubmit = async (e: React.FormEvent, isOtp = false, resendingOtp?: boolean) => {
        try {
            e.preventDefault()

            let validation = LoginValidations.getCustomerDataValidation(dataValidation)

            if (!validation.isValid)
                return showMessage(validation.message)

            if (resendingOtp)
                setResendingOtp(true)

            if (isOtp)
                setisSendingOtp(true)
            else
                setIsLoading(true)

            const exists = await LoginFunctions.customerExists(merchantSummary?.id, email, appAccessToken)

            if (!exists) {
                const redirect = searchParams.get('redirect')

                if (redirect)
                    router.push(`/criar-conta?email=${email}&redirect=${searchParams.get('redirect')}`)
                else
                    router.push(`/criar-conta?email=${email}`)

                setFullScreenLoading(true)
                return
            }
            else {
                if (!canTryToLogin) {
                    setCanTryToLogin(true)
                    return
                }
            }

            if (isOtp) {
                if (!otpCode) {
                    const sendOtpResponse = await CustomerService.OtpRequest(merchantSummary?.id!, email, appAccessToken)

                    if (!sendOtpResponse.success)
                        throw (sendOtpResponse.errorMessage)

                    if (resendingOtp) {
                        showMessage('Novo código enviado!')
                        setisNewOtpSended(true)
                    }

                    setOtpSuccess(true)
                    return
                }
            }

            let model: Customer.Response

            const signInModel: SignInProps = {
                email: email,
                password: password,
                merchantId: merchantSummary?.id,
            }

            const authCustomerProps: AuthCustomerProps = {
                merchantId: merchantSummary?.id,
                email: email,
                password: password,
                appAccessToken: appAccessToken
            }

            const resp = await LoginFunctions.authCustomer(authCustomerProps)
            model = resp.customer

            await LoginFunctions.signInCustomer({ ...signInModel, authResponse: resp.auth })

            setCustomer(model)

            redirectToRoute()
        }
        catch (e) {
            showMessage((e as Error).message, { type: 'danger', error: e as Error })
        }
        finally {
            setIsLoading(false)
            setisSendingOtp(false)
            setResendingOtp(false)
        }
    }

    const loginWithOtp = async (otp: string) => {
        try {
            if (!otp)
                return showMessage('Informe o código enviado ao seu e-mail.')

            if (otp.length !== 6)
                return showMessage('O código deve conter 6 digitos.')

            setIsLoading(true)

            const signInModel: SignInProps = {
                email: email,
                password: password,
                merchantId: merchantSummary?.id,
                isOtp: true
            }

            const authCustomerProps: AuthCustomerProps = {
                merchantId: merchantSummary?.id,
                email: email,
                password: password,
                appAccessToken: appAccessToken,
                otpCode: otp
            }

            const resp = await LoginFunctions.authCustomerWithOtp(authCustomerProps)

            await LoginFunctions.signInCustomer({ ...signInModel, authResponse: resp.auth })

            setCustomer(resp.customer)

            redirectToRoute()
        }
        catch (e) {
            showMessage((e as Error).message, { type: 'warning', error: e as Error })
        }
        finally {
            setIsLoading(false)
        }
    }

    const reset = () => {
        setEmail('')
        setPassword('')
        setCanTryToLogin(false)
        setOtpSuccess(false)
    }

    const redirectToRoute = () => {
        const redirect = searchParams.get('redirect')

        if (redirect) {
            setFullScreenLoading(true)
            router.push('/' + redirect)
        }
        else {
            if (callbackUrl)
                router.push(callbackUrl as string)
            else
                router.push('/')
            setFullScreenLoading(true)
        }
    }

    useEffect(() => {
        const _callbackUrl = getCookie(CookieNameEnum.CallbackUrl)

        if (_callbackUrl) {
            setCallbackUrl(_callbackUrl)
            deleteCookie(CookieNameEnum.CallbackUrl)
        }
    }, [])

    return (
        <>
            {fullScreenLoading && <LoadingFullscreen />}
            {
                forgotPassword &&
                <BottomSheet canClickOnOverlayToClose={window.innerHeight > window.innerWidth} isOpen={forgotPassword} onClose={() => setForgotPassword(false)}>
                    <ForgotPassword email={email} />
                </BottomSheet>
            }
            <NavBackTitle onArrowClick={canTryToLogin ? reset : undefined} title={otpSuccess ? 'Entrar com código' : 'Entrar'} />
            <section className={styles.container}>
                {
                    otpSuccess
                        ?
                        <OtpCode
                            email={email}
                            isLoading={isLoading}
                            resendingOtp={resendingOtp}
                            setOtpCode={setOtpCode}
                            submit={loginWithOtp}
                            back={() => setOtpSuccess(false)}
                            resendOtp={(e, isOtp) => handleSubmit(e, isOtp, true)}
                            isNewOtpSended={isNewOtpSended}
                            closeModalAfterNewCodeSended={() => setisNewOtpSended(false)}
                        />
                        :
                        <form className={styles.formArea} onSubmit={handleSubmit}>
                            <div className={styles.loginDataArea}>
                                <div className={styles.inputArea}>
                                    <label>E-mail</label>
                                    <ActionInput
                                        type="email"
                                        autoComplete='on'
                                        name="email"
                                        value={email}
                                        maxLength={100}
                                        onChange={(e) => setEmail(e.target.value)}
                                        classNameContainer={styles.dataInput}
                                    />
                                </div>
                                {
                                    canTryToLogin &&
                                    <div className={styles.inputArea}>
                                        <label>Senha</label>
                                        <ActionInput
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            classNameContainer={styles.dataInput}
                                            sideElement='checkbox'
                                            titleCheckbox='Mostrar'
                                            onCheck={() => setShowPassword(!showPassword)}
                                            checked={showPassword}
                                            disabled={false}
                                        />
                                    </div>
                                }
                                <ActionButton
                                    onClick={(e) => handleSubmit(e)}
                                    size='medium'
                                    isLoading={isLoading && !otpCode}
                                    className={`${styles.btn} font-xmd`}
                                    type='submit'
                                    disabled={isLoading || isSendingOtp}
                                >
                                    Continuar com e-mail
                                </ActionButton>
                            </div>
                            <AlternativeLoginMethods
                                handleSubmit={(e, isOtp) => handleSubmit(e, isOtp)}
                                canTryToLogin={canTryToLogin}
                                isSendingOtp={isSendingOtp}
                            />
                            {
                                canTryToLogin
                                    ?
                                    <div className={styles.forgotPassword}>
                                        <span className='font-sm'>Esqueceu a senha? <u onClick={() => setForgotPassword(true)}>Criar uma nova senha</u></span>
                                    </div>
                                    :
                                    <TermsOfUse />
                            }
                        </form>
                }
            </section>
        </>
    )
}